export const PDF_FONT_FILES = {
  NotoSans: 'NotoSans-Regular.ttf',
  NotoSansJP: 'NotoSansJP-Regular.ttf',
  NotoSansKR: 'NotoSansKR-Regular.ttf',
} as const;

export type EmbeddedPdfFont = keyof typeof PDF_FONT_FILES;
export type PdfFont = 'helvetica' | EmbeddedPdfFont;

// jsPDF's built-in Helvetica only has glyphs for Windows-1252.
const WINDOWS_1252 = /^[\t\n\r\x20-\x7E\xA0-\xFF€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]*$/;
const HANGUL = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/;
const JAPANESE = /[\u3000-\u30FF\u31F0-\u31FF\u3200-\u33FF\u3400-\u4DBF\u4E00-\u9FFF\uFF00-\uFFEF]/;
// Noto Sans lacks most symbols here (★ → ① ■) and Noto Sans JP has them, but JP lacks Latin Extended, Greek and Cyrillic letters, so letters win.
const SYMBOLS = /[\u2190-\u2BFF]/;
const NOTO_SANS_LETTERS = /[\u0100-\u024F\u0370-\u052F]/;

export const pdfFontFor = (text: string): PdfFont => {
  if (WINDOWS_1252.test(text)) return 'helvetica';
  if (HANGUL.test(text)) return 'NotoSansKR';
  if (JAPANESE.test(text)) return 'NotoSansJP';
  if (SYMBOLS.test(text) && !NOTO_SANS_LETTERS.test(text)) return 'NotoSansJP';
  return 'NotoSans';
};

// Noto Sans JP and KR have ASCII, Latin-1 and Windows-1252 except ˆ Š Ž ˜ š ž Ÿ.
const CJK_FACE_LATIN = /[\t\n\r\x20-\x7E\xA0-\xFF€‚ƒ„…†‡‰‹Œ‘’“”•–—™›œ]/;

const hasGlyph = (font: PdfFont, char: string): boolean => {
  switch (font) {
    case 'helvetica':
      return WINDOWS_1252.test(char);
    case 'NotoSans':
      return !HANGUL.test(char) && !JAPANESE.test(char) && !SYMBOLS.test(char);
    case 'NotoSansJP':
      return CJK_FACE_LATIN.test(char) || JAPANESE.test(char) || SYMBOLS.test(char);
    case 'NotoSansKR':
      return CJK_FACE_LATIN.test(char) || HANGUL.test(char) || JAPANESE.test(char) || SYMBOLS.test(char);
  }
};

const hasGlyphs = (font: PdfFont, text: string) => Array.from(text).every((char) => hasGlyph(font, char));

export type PdfTextRun = { font: PdfFont; text: string };

// Splits text that no single face covers ("カート名： Łódź", "Карандаш ★") into runs drawn in different faces.
// A wrapped line passes its whole string's font as base, so it picks the same faces as the string it came from.
export const pdfTextRuns = (text: string, base: PdfFont = pdfFontFor(text)): PdfTextRun[] => {
  if (hasGlyphs(base, text)) return [{ font: base, text }];
  const runs: PdfTextRun[] = [];
  for (const word of text.match(/\S*\s*/g) ?? []) {
    const wordFont = hasGlyphs(base, word) ? base : pdfFontFor(word);
    for (const char of Array.from(word)) {
      const font = hasGlyph(wordFont, char) ? wordFont : hasGlyph(base, char) ? base : pdfFontFor(char);
      const run = runs[runs.length - 1];
      if (run?.font === font) run.text += char;
      else runs.push({ font, text: char });
    }
  }
  return runs;
};

export const embeddedPdfFonts = (texts: string[]): EmbeddedPdfFont[] => {
  const fonts = new Set<EmbeddedPdfFont>();
  for (const text of texts) {
    for (const { font } of pdfTextRuns(text)) {
      if (font !== 'helvetica') fonts.add(font);
    }
  }
  return Array.from(fonts);
};
